import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, forkJoin, of } from 'rxjs';
import { PresalesService } from '../../services/presales.service';
import { PresalesTaskService } from '../../services/presales-task.service';
import { EmployeeService } from '../../services/employee.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AuthService } from '../../services/auth.service';
import {
  PresalesProject,
  PresalesTask,
  CreatePresalesTask,
  TrackerCard,
  PresalesProgress,
  PresalesProjectStatus,
  PRESALES_PROGRESS_OPTIONS,
  PRESALES_PROJECT_STATUS_OPTIONS,
  presalesProgressLabel
} from '../../models/presales.model';
import { Employee } from '../../models/employee.model';

/** One column of the board: a progress state and the cards sitting in it. */
interface TrackerColumn {
  label: string;
  value: PresalesProgress | 'Not Set';
  cssClass: string;
  cards: TrackerCard[];
}

@Component({
  selector: 'app-presale-activity-tracker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Select,
    MultiSelect,
    Tooltip,
    Dialog,
    ButtonModule,
    DatePicker,
    InputNumber,
    Checkbox,
    ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './presale-activity-tracker.component.html',
  styleUrl: './presale-activity-tracker.component.scss'
})
export class PresaleActivityTrackerComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private presalesService = inject(PresalesService);
  private taskService = inject(PresalesTaskService);
  private employeeService = inject(EmployeeService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);
  readonly pageHeaderService = inject(PageHeaderSearchActionService);

  loading = false;
  projects: PresalesProject[] = [];
  tasks: PresalesTask[] = [];
  employees: Employee[] = [];

  searchText = '';

  selectedOwners: string[] = [];
  selectedStatuses: PresalesProjectStatus[] = [];

  readonly statusOptions = PRESALES_PROJECT_STATUS_OPTIONS;
  readonly progressOptions = PRESALES_PROGRESS_OPTIONS;

  /** An empty column still says "nothing here yet", which is itself worth seeing. */
  hideEmptyColumns = false;

  // ---------- dialog ----------

  showTaskDialog = false;
  editingTaskId: string | null = null;
  form: CreatePresalesTask = this.emptyForm();
  saving = false;

  canManage = false;

  /** Set by the ?taskId=…&openForm=1 handoff; consumed once after the first load. */
  private pendingTaskId: string | null = null;
  private pendingOpenForm = false;

  constructor(private route: ActivatedRoute) {
    // Handoff from the Consolidated Board: /presales/activity-tracker?taskId=…&openForm=1.
    // Read in the constructor so the flags are set before load() resolves.
    const qp = this.route.snapshot.queryParamMap;
    this.pendingTaskId = qp.get('taskId');
    this.pendingOpenForm = qp.get('openForm') === '1';
  }

  ngOnInit(): void {
    this.pageHeaderService.clearPageConfig();
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search project or activity...');

    // Same roles that may edit a presales project may manage its activities.
    this.authService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.canManage = !!(user?.isAdmin || user?.isPM);
      });

    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(text => {
        this.searchText = text ?? '';
      });

    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  load(): void {
    this.loading = true;

    // One pass: the board needs both halves before it can lay out a column, so a partial
    // render would only flash an incomplete board.
    forkJoin({
      projects: this.presalesService.getAllProjects().pipe(catchError(() => of([] as PresalesProject[]))),
      tasks: this.taskService.getAll().pipe(catchError(() => of([] as PresalesTask[]))),
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[])))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ projects, tasks, employees }) => {
        this.projects = projects ?? [];
        this.tasks = tasks ?? [];
        this.employees = (employees ?? []).filter(e => e.isActive);
        this.loading = false;

        // Deferred until here: the edit dialog is populated from the loaded task, and its
        // project/owner dropdowns need the lists beside it.
        this.consumePendingTaskHandoff();
      });
  }

  /**
   * Completes the handoff: opens the edit dialog on the requested activity.
   *
   * Consumed once and cleared either way, so a later reload cannot reopen a stale activity.
   */
  private consumePendingTaskHandoff(): void {
    if (!this.pendingOpenForm || !this.pendingTaskId) return;

    const taskId = this.pendingTaskId;
    this.pendingOpenForm = false;
    this.pendingTaskId = null;

    // Editing is gated the same way the board's own cards are: a viewer who may not manage
    // activities lands on the board rather than in a form they cannot save.
    if (!this.canManage) return;

    const task = this.tasks.find(t => t.id === taskId);
    if (!task) {
      // Deleted between the two screens. Saying so beats opening nothing.
      this.messageService.add({
        severity: 'warn',
        summary: 'Activity unavailable',
        detail: 'That activity could not be found.'
      });
      return;
    }

    this.editTask(task);
  }

  // ---------- board ----------

  /** Projects and tasks flattened to one shape, then filtered. */
  get cards(): TrackerCard[] {
    const search = this.searchText.trim().toLowerCase();

    // Activities only. The board's job is "what presales work is outstanding, and who owns
    // it" across every deal — a question the per-project Activities tab cannot answer.
    // Projects used to appear alongside them, which made the board a second project list
    // and left its cards unclickable.
    const all: TrackerCard[] = this.tasks.map(t => ({
      kind: 'task' as const,
      id: t.id,
      name: t.name,
      subtitle: t.presalesProjectName,
      // Derived server-side from the parent project, so the card needs no extra lookup.
      clientName: t.clientName,
      description: t.description,
      comments: t.comments,
      ownerName: t.ownerName,
      progress: t.progress ?? 'To Start',
      status: t.status ?? null,
      progressPercentage: t.progressPercentage ?? 0,
      startDate: t.startDate ?? null,
      targetDate: t.targetDate ?? null,
      source: t
    }));

    return all.filter(card => {
      if (search) {
        // Everything shown on a card is searchable, so what you can read you can find.
        const matches = card.name.toLowerCase().includes(search)
          || (card.subtitle ?? '').toLowerCase().includes(search)
          || (card.clientName ?? '').toLowerCase().includes(search)
          || (card.description ?? '').toLowerCase().includes(search)
          || (card.comments ?? '').toLowerCase().includes(search)
          || (card.ownerName ?? '').toLowerCase().includes(search);
        if (!matches) {
          return false;
        }
      }

      if (this.selectedOwners.length && !this.selectedOwners.includes(card.ownerName ?? '')) {
        return false;
      }

      if (this.selectedStatuses.length
        && !this.selectedStatuses.includes(card.status as PresalesProjectStatus)) {
        return false;
      }

      return true;
    });
  }

  /**
   * Columns keyed on progress, matching the deal board so the two read alike. "Not Set"
   * catches anything carrying a value outside the known list rather than dropping it.
   */
  get columns(): TrackerColumn[] {
    const cards = this.cards;

    const known: TrackerColumn[] = this.progressOptions.map(option => ({
      label: option.label,
      value: option.value,
      cssClass: this.progressClass(option.value),
      cards: cards.filter(c => c.progress === option.value)
    }));

    const knownValues = this.progressOptions.map(o => o.value);
    const unset = cards.filter(c => !knownValues.includes(c.progress));

    const all = unset.length
      ? [...known, { label: 'Not Set', value: 'Not Set' as const, cssClass: 'progress-unset', cards: unset }]
      : known;

    return this.hideEmptyColumns ? all.filter(c => c.cards.length > 0) : all;
  }

  /** Up to two initials for a card avatar; '?' when the name is missing. */
  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  /**
   * Deterministic avatar tint from a name. Identical to the deal board's, deliberately: the
   * same person must get the same colour on both boards, or the tint stops meaning anything.
   */
  avatarHue(name?: string | null): number {
    const s = name || '';
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
    return [210, 150, 275, 25, 340, 190][h % 6];
  }

  /** Overdue activities within one column, for the subtotal under its title. */
  columnOverdueCount(col: TrackerColumn): number {
    return col.cards.filter(c => this.overdueDays(c) !== null).length;
  }

  /** Activities currently on the board, after filters. */
  get totalCardCount(): number {
    return this.cards.length;
  }

  /** Past their end date and not yet closed — the count worth acting on. */
  get overdueCardCount(): number {
    return this.cards.filter(c => this.overdueDays(c) !== null).length;
  }

  /** Activities with nobody driving them. */
  get unassignedCardCount(): number {
    return this.cards.filter(c => !c.ownerName).length;
  }

  /** Owners on the board, so the dropdown never offers a name with nothing behind it. */
  get ownerOptions(): { label: string; value: string }[] {
    const names = new Set<string>();
    for (const card of [...this.projects.map(p => p.managerName), ...this.tasks.map(t => t.ownerName)]) {
      if (card) {
        names.add(card);
      }
    }
    return [...names].sort().map(name => ({ label: name, value: name }));
  }

  get employeeOptions(): { label: string; value: string }[] {
    return this.employees
      .map(e => ({ label: e.name, value: e.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get projectOptions(): { label: string; value: string | null }[] {
    return [
      { label: 'No project — standalone', value: null },
      ...this.projects
        .map(p => ({ label: p.name, value: p.id as string | null }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
  }

  get hasActiveFilters(): boolean {
    return !!this.searchText.trim()
      || this.selectedOwners.length > 0
      || this.selectedStatuses.length > 0;
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedOwners = [];
    this.selectedStatuses = [];
    this.pageHeaderService.setSearchText('');
  }

  // ---------- card actions ----------

  onCardClick(card: TrackerCard): void {
    // Only tasks are editable here. A project card opens its own details page, which owns
    // the fuller edit form — duplicating it in this dialog would be a second place to keep
    // in step.
    if (this.canManage) {
      this.editTask(card.source as PresalesTask);
    }
  }

  newTask(): void {
    this.editingTaskId = null;
    this.form = this.emptyForm();
    this.startDateValue = null;
    this.targetDateValue = null;
    this.showTaskDialog = true;
  }

  editTask(task: PresalesTask): void {
    this.editingTaskId = task.id;
    this.form = {
      presalesProjectId: task.presalesProjectId ?? null,
      name: task.name,
      description: task.description ?? null,
      startDate: task.startDate ?? null,
      targetDate: task.targetDate ?? null,
      ownerId: task.ownerId ?? null,
      assignedToId: task.assignedToId ?? null,
      progress: task.progress,
      progressPercentage: task.progressPercentage ?? 0,
      status: task.status ?? null,
      comments: task.comments ?? null,
      isActive: task.isActive,
      isAllocatable: task.isAllocatable ?? false
    };
    // Seed the pickers once, from the stored ISO strings.
    this.startDateValue = task.startDate ? new Date(task.startDate) : null;
    this.targetDateValue = task.targetDate ? new Date(task.targetDate) : null;
    this.showTaskDialog = true;
  }

  /**
   * The pickers bind to these Date fields directly; the ISO strings the API wants are
   * produced once, on save.
   *
   * They are plain fields, NOT getters over form.startDate/targetDate. A getter returning
   * `new Date(...)` hands change detection a different object on every read, so Angular
   * sees the model as perpetually changed and the calendar locks the page up. Holding the
   * Date means the reference is stable between cycles.
   */
  startDateValue: Date | null = null;
  targetDateValue: Date | null = null;

  /**
   * Keeps the two dates in a sensible order. The End picker's minDate already stops a date
   * before the start being chosen; this covers moving the START forward past an end date
   * that was already set, which minDate cannot catch.
   */
  onStartDateChange(): void {
    if (this.startDateValue && this.targetDateValue
      && this.targetDateValue < this.startDateValue) {
      this.targetDateValue = null;
    }
  }

  /**
   * Client, Presale PM and Cost Center for the project currently chosen in the dialog.
   * Read straight off the loaded project — they belong to it, so the activity shows them
   * rather than storing its own copy that could drift.
   *
   * Null for a standalone activity, which hides the block entirely.
   */
  get selectedProjectContext(): PresalesProject | null {
    if (!this.form.presalesProjectId) {
      return null;
    }

    // Returns the project itself rather than a freshly built object. A getter that returns
    // a new literal each call hands change detection a different reference every cycle,
    // which is what made the date picker lock the page; the template reads the three fields
    // it needs straight off the project.
    return this.projects.find(p => p.id === this.form.presalesProjectId) ?? null;
  }

  saveTask(): void {
    if (!this.form.name?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Name required',
        detail: 'Give the activity a name before saving.'
      });
      return;
    }

    // The pickers hold Dates; the API takes date-only ISO strings. Converted here, once,
    // rather than on every read of a getter.
    this.form.startDate = this.startDateValue ? this.toIsoDate(this.startDateValue) : null;
    this.form.targetDate = this.targetDateValue ? this.toIsoDate(this.targetDateValue) : null;

    this.saving = true;

    const request = this.editingTaskId
      ? this.taskService.update(this.editingTaskId, this.form)
      : this.taskService.add(this.form);

    request
      .pipe(
        catchError(error => {
          // A 422 carries a business rule the user can act on — "needs a Sales Owner
          // before it can be made available in Monthly Allocation", say. Showing the
          // generic retry line instead would hide the one thing they need to read.
          const detail = error?.error?.details
            ?? 'The activity was not saved. Please try again.';
          this.messageService.add({
            severity: 'error',
            summary: 'Could not save',
            detail
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.saving = false;
        if (!result) {
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: this.editingTaskId ? 'Updated' : 'Added',
          detail: `"${result.name}" has been saved.`
        });

        this.showTaskDialog = false;
        this.load();
      });
  }

  /** Deletes whatever the dialog is editing. Only reachable while editingTaskId is set. */
  confirmDeleteCurrent(): void {
    const id = this.editingTaskId;
    if (!id) {
      return;
    }

    const name = this.form.name || 'this activity';

    this.confirmationService.confirm({
      message: `Delete "${name}"? This cannot be undone.`,
      header: 'Delete activity',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteTask(id, name)
    });
  }

  private deleteTask(id: string, name: string): void {
    this.taskService
      .delete(id)
      .pipe(
        catchError(() => {
          this.messageService.add({
            severity: 'error',
            summary: 'Could not delete',
            detail: 'The activity was not deleted. Please try again.'
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Deleted',
          detail: `"${name}" has been removed.`
        });
        this.showTaskDialog = false;
        this.load();
      });
  }

  // ---------- display helpers ----------

  progressLabel(progress: PresalesProgress): string {
    return presalesProgressLabel(progress);
  }

  /** Column tint, matching the deal board's vocabulary. */
  /**
   * Column class names, matching the `progress-*` ones the deal board uses. They have to be
   * exactly these: the shared accent bars and dot colours in _presales.scss key on them, so
   * the board's own `col-*` names rendered every column plain grey.
   */
  progressClass(progress: PresalesProgress | 'Not Set'): string {
    switch (progress) {
      case 'To Start': return 'progress-to-start';
      case 'InProgress': return 'progress-inprogress';
      case 'On Hold': return 'progress-on-hold';
      case 'Closed': return 'progress-closed';
      default: return 'progress-unset';
    }
  }

  /**
   * RAG dot class. Must be the shared `status-badge-*` names: .k-rag in _presales.scss
   * colours on those, so the board's own `rag-*` names left every health dot grey.
   */
  statusClass(status: PresalesProjectStatus | null | undefined): string {
    switch (status) {
      case 'Green': return 'status-badge-green';
      case 'Amber': return 'status-badge-amber';
      case 'Red': return 'status-badge-red';
      default: return 'status-badge-default';
    }
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  /** Days a target date is past, or null when it is not overdue. Closed work never is. */
  overdueDays(card: TrackerCard): number | null {
    if (!card.targetDate || card.progress === 'Closed') {
      return null;
    }
    const target = new Date(card.targetDate);
    if (Number.isNaN(target.getTime())) {
      return null;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - target.getTime()) / 86_400_000);
    return days > 0 ? days : null;
  }

  private emptyForm(): CreatePresalesTask {
    return {
      presalesProjectId: null,
      name: '',
      description: null,
      startDate: null,
      targetDate: null,
      ownerId: null,
      assignedToId: null,
      progress: 'To Start',
      progressPercentage: 0,
      status: null,
      comments: null,
      isActive: true,
      // Ticked for a NEW activity: staffing one is the normal case, and an allocator who
      // cannot find it in the dropdown gets no hint as to why — the box lives on another
      // screen. Untick it for an activity nobody books time against. Only the blank form
      // is affected; an existing activity keeps whatever it was saved with.
      isAllocatable: true
    };
  }

  /** Date-only ISO, so a timezone offset cannot shift a target date by a day. */
  private toIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
